import React, { useEffect, useRef } from 'react'
import { AuthLayoutProps } from '../types'
import BackButton from './BackButton'
import { AUTH_PAGE_CARDS } from '../constants/data'

const AuthLayout: React.FC<AuthLayoutProps> = ({ children }) => {
  const mobileScrollRef = useRef<HTMLDivElement>(null)
  const desktopScrollRef = useRef<HTMLDivElement>(null)

  // Auto scroll logic for both mobile and desktop
  const setupAutoScroll = (ref: React.RefObject<HTMLDivElement>) => {
    const scrollContainer = ref.current
    if (!scrollContainer) return

    let scrollInterval: NodeJS.Timeout
    let isHovered = false

    const startAutoScroll = () => {
      scrollInterval = setInterval(() => {
        if (!isHovered && scrollContainer) {
          const isAtEnd = scrollContainer.scrollLeft >= (scrollContainer.scrollWidth - scrollContainer.clientWidth) / 2
          if (isAtEnd) {
            // Reset to one-third when reaching two-thirds
            scrollContainer.scrollTo({ left: scrollContainer.scrollWidth / 3, behavior: 'auto' })
          } else {
            scrollContainer.scrollBy({ left: 1, behavior: 'auto' })
          }
        }
      }, 30)
    }

    const handleMouseEnter = () => { isHovered = true }
    const handleMouseLeave = () => { isHovered = false }

    scrollContainer.addEventListener('mouseenter', handleMouseEnter)
    scrollContainer.addEventListener('mouseleave', handleMouseLeave)
    startAutoScroll()

    return () => {
      clearInterval(scrollInterval)
      scrollContainer.removeEventListener('mouseenter', handleMouseEnter)
      scrollContainer.removeEventListener('mouseleave', handleMouseLeave)
    }
  }

  useEffect(() => {
    const mobileCleanup = setupAutoScroll(mobileScrollRef)
    const desktopCleanup = setupAutoScroll(desktopScrollRef)

    return () => {
      mobileCleanup?.()
      desktopCleanup?.()
    }
  }, [])

  // Create three sets of cards for infinite scroll effect
  const repeatedCards = [...AUTH_PAGE_CARDS, ...AUTH_PAGE_CARDS, ...AUTH_PAGE_CARDS]

  return (
    <>
      {/* Mobile layout */}
      <div className="lg:hidden bg-primary overflow-y-auto h-screen">
        {/* Header */}
        <div className="pt-4">
          <BackButton />
        </div>

        {/* Content Area */}
        <div className="min-h-[calc(100vh-56px)] overflow-y-auto">
          {/* Form Section */}
          <div>
            {children}
          </div>

          {/* Bottom Section */}
          <div>
            {/* Heading */}
            <div className="mt-4">
              <h1 className="text-xl text-center font-medium text-secondaryNeutral-1 leading-snug">
                One platform.
                <br />
                <span className="block">Infinite possibilities.</span>
              </h1>
            </div>

            {/* Cards - Horizontal Scroll with auto-scroll */}
            <div 
              ref={mobileScrollRef}
              className="py-5 px-4 overflow-x-auto scrollbar-hide"
            >
              <div className="flex gap-4 min-w-max">
                {repeatedCards.map((card, index) => (
                  <img 
                    key={`${card.id}-${index}`}
                    src={card.image}
                    alt={card.alt} 
                    className="h-32 w-auto object-contain"
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop layout */}
      <div className="hidden lg:block h-screen w-screen p-3 relative bg-secondaryNeutral-1">
        <div className="flex flex-col md:flex-row rounded-lg bg-secondaryNeutral-5 h-full w-full overflow-hidden">
          {/* Left side - Form */}
          <div className="h-full flex items-center justify-center w-full md:w-1/2 px-[120px] py-8 overflow-y-auto overflow-x-hidden">
            {children}
          </div>

          {/* Right side - Branding */}
          <div className="hidden md:flex flex-col w-full md:w-1/2 p-8 bg-primaryAccent-9">
            {/* Logo */}
            <div className="flex justify-center mb-8">
              <img 
                src="/kai-logo.svg"
                alt="kai-logo" 
                className="h-8 w-auto"
              />
            </div>
            
            {/* Cards Container */}
            <div className="flex-1 flex flex-col items-center justify-center">
              <div 
                ref={desktopScrollRef}
                className="w-full max-w-2xl px-4 overflow-x-auto scrollbar-hide"
              >
                <div className="flex gap-4 min-w-max">
                  {repeatedCards.map((card, index) => (
                    <img 
                      key={`${card.id}-${index}`}
                      src={card.image}
                      alt={card.alt} 
                      className="w-[280px] object-contain"
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Heading */}
            <div className="mt-8">
              <h1 className="text-2xl md:text-3xl text-center font-medium text-secondaryNeutral-1 leading-snug">
                One platform.
                <br />
                <span className="block">Infinite possibilities.</span>
              </h1>
            </div>
          </div>    
        </div>     
      </div>
    </>
  )
}

export default AuthLayout
